/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding UTF-8 Consumer key attributes.
 *
 * @author VJAYA
 * 09/06/2024
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the UTF-8 Consumer Key form
 */
class ConsumerKeyForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            propertyId: null,
            name: '',
            value: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'institute',
                    inputName: 'instituteName',
                    required: true,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'Partner',
                    inputName: 'partnerName',
                    required: true,
                    disabled: true
                }
            ]
        };

        // get the parameter from URL
        const propertyId = this.props.match.params.id;

        // set icons to the header
        if (propertyId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'utf-enabled-consumer-key',
                pageName: "Consumer Key",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/utf-enabled-consumer-key",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'utf-enabled-consumer-key',
                pageName: "Consumer Key",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/utf-enabled-consumer-key",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deleteConsumerKey);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        // get parameter from URL
        const propertyId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create partner
        if (propertyId !== undefined) {

            const url = '/utf-enabled-consumer-key?search=%7Bid:' + propertyId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: this.source.token})
                .then(res => {
                    const data = res.data[0];
                    console.log("Fetched Data:", data);
                    oldState.propertyId = (data.id !== undefined) ? data.id : null;
                    oldState.name = (data.name !== undefined) ? data.name : null;
                    oldState.partnerName = (data.partnerName !== undefined) ? data.partnerName : '';
                    oldState.instituteName = (data.instituteName !== undefined) ? data.instituteName : '';
                    oldState.value = (data.value !== undefined) ? data.value : '';
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                }).catch(err => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(err);
                });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deleteConsumerKey);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel UTF-8 Consumer key async calls");
    };





    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.propertyId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'application-properties',
                name: this.state.name
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Consumer Key Details",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a Consumer Key
     * @param data
     */
    deleteConsumerKey = async (data) => {

        console.log("Property ID before deletion:", this.state.propertyId);

        if (data && (this.props.match.path === "/home/utf-enabled-consumer-key/:id")) {
            await API.delete('/utf-enabled-consumer-key?id=' + this.state.propertyId, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "UTF-8 encoding has been disabled for the Consumer key.");
                    this.props.history.push('/home/utf-enabled-consumer-key');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Consumer Key is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.name = "";
        newState.value = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/utf-enabled-consumer-key');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Delete" onClick={this.deleteConsumerKey}>
                        <i className="fa fa-ban fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state}/>
                </form>
            </div>
        )
    }
}

export default ConsumerKeyForm;
